// ─────────────────────────────────────────────────────────────────────────────
// Helpers pour la configuration remote de massia tabulator et les requêtes API
// ─────────────────────────────────────────────────────────────────────────────

export interface MassiaFilter {
	criterion: string;
	type: string;
	value: unknown;
}

export interface MassiaSort {
	criterion: string;
	isDescending: boolean;
}

export interface MassiaPagination {
	take: number;
	skip: number;
}

export interface MassiaRequestParams {
	filters: MassiaFilter[];
	sorts: MassiaSort[];
	pagination: MassiaPagination;
}

/** Correspondance opérateurs Tabulator → types Massia */
const TABULATOR_FILTER_TYPE_MAP: Record<string, string> = {
	like: 'Contains',
	'=': 'IsEqualTo',
	'!=': 'IsNotEqualTo',
	'>': 'IsGreaterThan',
	'>=': 'IsGreaterThanOrEqualTo',
	'<': 'IsLessThan',
	'<=': 'IsLessThanOrEqualTo',
	starts: 'StartsWith',
	ends: 'EndsWith'
};

/**
 * Convertit les paramètres bruts envoyés par Tabulator (ajaxRequestFunc)
 * en objets filters / sorts / pagination compatibles avec l'API Massia.
 *
 * @example
 * fetchFn: (url, config, params) => {
 *     const { filters, sorts, pagination } = buildMassiaRequestParams(params);
 *     return MyService.getData(filters, sorts, pagination, ...extraParams);
 * }
 */
export function buildMassiaRequestParams(params: Record<string, unknown>, defaultPageSize = 200): MassiaRequestParams {
	// ── Sorts ────────────────────────────────────────────────────────────────
	const sorts: MassiaSort[] = ((params.sort as Array<{ field: string; dir: string }>) ?? []).map((s) => ({
		criterion: s.field,
		isDescending: s.dir === 'desc'
	}));

	// ── Filters ──────────────────────────────────────────────────────────────
	const filters: MassiaFilter[] = [];
	for (const f of (params.filter as Array<{ field: string; value: unknown; type?: string }>) ?? []) {
		if (Array.isArray(f.value)) {
			// Filtre composite (ex: range date ou nombre retourné par makeDateRangeFilter / makeNumberRangeFilter)
			for (const v of f.value as Array<{ type?: string; operator?: string; value: unknown }>) {
				filters.push({
					criterion: f.field,
					type: v.type ?? (v.operator ? TABULATOR_FILTER_TYPE_MAP[v.operator] : null) ?? 'Contains',
					value: v.value
				});
			}
		} else {
			filters.push({
				criterion: f.field,
				type: (f.type ? TABULATOR_FILTER_TYPE_MAP[f.type] : null) ?? 'Contains',
				value: f.value ?? ''
			});
		}
	}

	// ── Pagination ───────────────────────────────────────────────────────────
	const size = (params.size as number | undefined) ?? defaultPageSize;
	const page = (params.page as number | undefined) ?? 1;
	const pagination: MassiaPagination = {
		take: size,
		skip: (page - 1) * size
	};

	return { filters, sorts, pagination };
}

/**
 * Formate la réponse API Massia en objet attendu par Tabulator (ajaxResponse).
 * Compatible avec la pagination remote de Tabulator.
 */
export function buildTabulatorResponse(
	response: Record<string, unknown> | unknown[],
	pageSize: number
): { data: unknown[]; last_page: number; last_row: number } {
	const respObj = response as Record<string, unknown>;
	const items: unknown[] = (respObj.items as unknown[]) ?? (Array.isArray(response) ? response : []);
	const total: number = (respObj.total as number) ?? items.length;
	return {
		data: items,
		last_page: Math.ceil(total / pageSize) || 1,
		last_row: total
	};
}

export const MassiaTabulatorParamsHelpers = {
	buildMassiaRequestParams,
	buildTabulatorResponse
};
